/**
 * Module dependencies.
 */

import { DisclosureName, useFiltersDisclosures } from 'src/context/filters-disclosures/context';
import { availableFiltersKeys, availableFiltersValues } from 'src/core/collections-config/filters';
import { color, media, units } from 'src/styles/utils';
import { ifProp } from 'styled-tools';
import { slipResistanceKeys } from 'src/core/collections-config/slip-resistance';
import { useFiltersList } from 'src/context/filters-list/context';
import { useRouter } from 'next/router';
import { useTranslate } from 'src/core/utils/translator';
import Button from 'src/components/core/buttons/button';
import FiltersSidebar from './filters-sidebar';
import React, { ReactElement, useCallback, useEffect, useState } from 'react';
import camelCase from 'lodash/camelCase';
import closeIcon from 'src/assets/svg/close.svg';
import filter from 'lodash/filter';
import filterIcon from 'src/assets/svg/filter.svg';
import get from 'lodash/get';
import head from 'lodash/head';
import isEmpty from 'lodash/isEmpty';
import qs from 'qs';
import size from 'lodash/size';
import split from 'lodash/split';
import styled from 'styled-components';
import useAllCollections from 'src/api/app/collections/use-all-collections';
import useBodyScroll from 'src/hooks/use-body-scroll';
import useProductApplications from 'src/api/app/products/use-product-applications';
import useProductBaseFormats from 'src/api/app/products/use-product-base-formats';
import useProductThicknesses from 'src/api/app/products/use-product-thicknesses';

/**
 * Filter buttons.
 */

const filterButtons = [
  availableFiltersKeys.effect,
  availableFiltersKeys.color,
  availableFiltersKeys.thickness,
  availableFiltersKeys.format,
  availableFiltersKeys.recycleMaterial
];

/**
 * `Props` type.
 */

type Props = {
  className?: string;
  isKiosk?: boolean;
  isUpdating: boolean;
  isUpdatingSucceed: boolean;
  onUpdate: () => void;
};

/**
 * `Wrapper` styled component.
 */

const Wrapper = styled.div`
  margin-bottom: ${units(8)};
  overflow: hidden;
  position: relative;

  ${media.max('ms')`
    white-space: nowrap;

    &::after {
      background-image: linear-gradient(to right, ${color.transparentize('beige100', 0)}, ${color('beige100')} 100%);
      content: '';
      height: ${units(7)};
      position: absolute;
      right: 0;
      top: 0;
      width: ${units(7)};
    }
  `}
`;

/**
 * `Grid` styled component.
 */

const Grid = styled.div<Pick<Props, 'isKiosk'>>`
  display: grid;
  grid-auto-columns: min-content;
  grid-auto-flow: column;
  grid-gap: ${units(2)};
  overflow-x: auto;
  padding-bottom: ${units(2)};
  scroll-snap-type: x mandatory;

  ${media.min('ms')`
    display: flex;
    grid-gap: 0;
    flex-flow: row wrap;
    justify-content: flex-start;
    padding-bottom: 0;

    > * {
      margin-bottom: ${units(2)};
      margin-right: ${ifProp('isKiosk', units(1), units(2))};
    }
  `}
`;

/**
 * `useCollectionsFilters` hook.
 */

function useCollectionsFilters() {
  const { data: collections } = useAllCollections();
  const { data: applications } = useProductApplications();
  const { data: formats } = useProductBaseFormats();
  const { data: thicknesses } = useProductThicknesses();
  const { translate } = useTranslate();
  const getFilterTranslation = useCallback(
    (filterType: string, value: string) => {
      const keyInTranslations = camelCase(value);

      switch (filterType) {
        case availableFiltersKeys.collectionsAll:
        case availableFiltersKeys.collectionsNew:
          return get(head(filter(collections, { id: value })), 'name');

        case availableFiltersKeys.application:
          return get(head(filter(applications, { id: value })), 'name');

        case availableFiltersKeys.thickness:
          return translate('collections:list.filterTags.thickness', {
            thickness: get(head(filter(thicknesses, { id: value })), 'name')
          });

        case availableFiltersKeys.color:
          return translate(`collections:colors.${keyInTranslations}`);

        case availableFiltersKeys.effect:
          return translate(`collections:effects.${keyInTranslations}`);

        case availableFiltersKeys.finish:
          return translate(`collections:finishes.${keyInTranslations}`);

        case availableFiltersKeys.format:
          return get(head(filter(formats, { name: value })), 'name');

        case availableFiltersKeys.typology:
          return translate(`collections:typologies.${keyInTranslations}`);

        case availableFiltersKeys.trafficLevel:
          return translate('collections:list.filterTags.trafficLevel', {
            level: translate(`collections:trafficLevel.${value}`)
          });

        case availableFiltersKeys.recycleMaterial:
          return translate('collections:list.filterTags.recycleMaterial', {
            value: `${value}%`
          });

        case availableFiltersKeys.hueVariation:
          return translate('collections:list.filterTags.hueVariation', {
            hue: translate(`collections:hueVariation.${value}`)
          });

        default:
          return value;
      }
    },
    [applications, collections, formats, thicknesses, translate]
  );

  const existValue = useCallback(
    (filterType: string, value: string) => {
      switch (filterType) {
        case availableFiltersKeys.collectionsAll:
        case availableFiltersKeys.collectionsNew:
          return size(filter(collections, { id: value })) > 0;

        case availableFiltersKeys.application:
          return size(filter(applications, { id: value })) > 0;

        case availableFiltersKeys.format:
          return size(filter(formats, { name: value })) > 0;

        case availableFiltersKeys.thickness:
          return size(filter(thicknesses, { id: value })) > 0;

        case availableFiltersKeys.color:
        case availableFiltersKeys.effect:
        case availableFiltersKeys.finish:
        case availableFiltersKeys.hueVariation:
        case availableFiltersKeys.trafficLevel:
        case availableFiltersKeys.recycleMaterial:
        case availableFiltersKeys.typology:
        case slipResistanceKeys.ansiA137Dcof:
        case slipResistanceKeys.bs797622002:
        case slipResistanceKeys.dim51097:
        case slipResistanceKeys.dim51130:
        case slipResistanceKeys.pei:
        case slipResistanceKeys.uneEnv126332003:
        case slipResistanceKeys.upec:
          return Object.values(availableFiltersValues[filterType]).includes(value);

        default:
          return false;
      }
    },
    [applications, collections, formats, thicknesses]
  );

  return { existValue, getFilterTranslation };
}

/**
 * `Filters` component.
 */

const Filters = (props: Props): ReactElement => {
  const { className, isKiosk, isUpdating, onUpdate } = props;
  const { translate } = useTranslate();
  const { existValue, getFilterTranslation } = useCollectionsFilters();
  const router = useRouter();
  const [isOpen, setOpen] = useState<boolean>();
  const { onSubmitRemove, submittedFilters } = useFiltersList();
  const { onCloseAll, onOpenAndCloseOthers } = useFiltersDisclosures();
  const handleOpenFilter = useCallback(
    (name: DisclosureName) => {
      onOpenAndCloseOthers(name);
      setOpen(true);
    },
    [onOpenAndCloseOthers]
  );

  const handleRemoveFilter = useCallback(
    (filterType, value) => {
      const { submittedFilters } = onSubmitRemove(filterType, value);

      const queryString = isEmpty(submittedFilters) ? '' : `?${qs.stringify({ filter: submittedFilters })}`;

      router
        .push(
          `${router.pathname}${queryString}`,
          undefined,
          { shallow: true }
        )
        .then(onUpdate);
    },
    [onSubmitRemove, onUpdate, router]
  );

  useBodyScroll({ off: isOpen });

  useEffect(() => {
    if (!isOpen) {
      const timeout = setTimeout(() => {
        onCloseAll();
      }, 500);

      return () => clearTimeout(timeout);
    }
  }, [isOpen, onCloseAll]);

  useEffect(() => {
    if (isOpen) {
      const handleEscKey = (event: KeyboardEvent) => {
        if (event.which === 27) {
          setOpen(false);
        }
      };

      document.addEventListener('keydown', handleEscKey, false);

      return () => {
        document.removeEventListener('keydown', handleEscKey, false);
      };
    }
  }, [isOpen, setOpen]);

  return (
    <>
      <Wrapper className={className}>
        <Grid isKiosk={isKiosk}>
          <Button icon={filterIcon} onClick={() => setOpen(true)} variant={'outlined'}>
            {translate('common:labels.filter')}
          </Button>

          {isEmpty(submittedFilters)
            ? filterButtons.map((filterKey) => {
                // Map filter keys to translation keys (some use snake_case in API but camelCase in translations)
                const translationKey = filterKey === 'recycle_material' ? 'recycleMaterial' : filterKey;

                return (
                  <Button key={filterKey} onClick={() => handleOpenFilter(filterKey)} variant={'outlined'}>
                    {translate(`common:labels.${translationKey}`)}
                  </Button>
                );
              })
            : Object.entries(submittedFilters).map(([filterType, filterValue]) =>
                split(filterValue, ',').map((value: string) => {
                  return (
                    existValue(filterType, value) && (
                      <Button
                        icon={closeIcon}
                        key={value}
                        onClick={() => handleRemoveFilter(filterType, value)}
                        variant={'outlined'}
                      >
                        {getFilterTranslation(filterType, value)}
                      </Button>
                    )
                  );
                })
              )}
        </Grid>
      </Wrapper>

      <FiltersSidebar isOpen={isOpen} isUpdating={isUpdating} onClose={() => setOpen(false)} onUpdate={onUpdate} />
    </>
  );
};

/**
 * Export `Filters` component.
 */

export default Filters;
